import {
  DEFAULT_WEBSITE_ANIMATIONS,
  createEmptyLocalized,
} from "./options"
import {
  WULOOJ_BRAND,
  WULOOJ_CONTACT,
  WULOOJ_CONTENT,
  WULOOJ_FOOTER,
  WULOOJ_HERO,
  WULOOJ_SEO,
  WULOOJ_SOCIAL,
  WULOOJ_THEME,
  WULOOJ_TOP_AD,
} from "./websiteConfigBootstrap"

export const BUILDER_TEMPLATE_ID = "addoha-template"

/** Layout templates selectable in the builder (must match storefront implementation). */
export const BUILDER_AVAILABLE_LAYOUT_IDS = new Set([
  BUILDER_TEMPLATE_ID,
  "classic-storefront",
  "wulooj-minimal",
])

export const HERO_LAYOUT_OPTIONS = [
  {
    id: "carousel",
    label: "Banner carousel",
    description: "Full-width carousel using menu banner images.",
  },
  {
    id: "split",
    label: "Split hero",
    description: "Headline and button on the left, banner image on the right.",
  },
]

const VALID_HERO_LAYOUTS = new Set(HERO_LAYOUT_OPTIONS.map(option => option.id))

export const BUILDER_SECTION_IDS = [
  "orderContextBar",
  "hero",
  "offerBanner",
  "categoryGrid",
  "featuredItems",
  "offerItems",
  "whatsappFab",
]

/** Preserve tenant order and append any missing home sections at the end. */
export const normalizeHomeSectionOrder = (
  order = [],
  fallbackOrder = BUILDER_SECTION_IDS
) => {
  const allowed = new Set(BUILDER_SECTION_IDS)
  const seen = new Set()
  const next = []

  if (Array.isArray(order)) {
    order.forEach(id => {
      const key = String(id || "").trim()
      if (!allowed.has(key) || seen.has(key)) return
      seen.add(key)
      next.push(key)
    })
  }

  ;[...fallbackOrder, ...BUILDER_SECTION_IDS].forEach(id => {
    if (!seen.has(id) && allowed.has(id)) {
      seen.add(id)
      next.push(id)
    }
  })

  return next.length ? next : [...BUILDER_SECTION_IDS]
}

/** Full social keys kept in config schema; storefront footer uses `footer.social.platforms` instead. */
export const SOCIAL_FIELDS = [
  { key: "facebook", label: "Facebook" },
  { key: "instagram", label: "Instagram" },
  { key: "twitter", label: "X / Twitter" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "youtube", label: "YouTube" },
  { key: "tiktok", label: "TikTok" },
  { key: "snapchat", label: "Snapchat" },
  { key: "whatsapp", label: "WhatsApp" },
]

/** Only WhatsApp URL is consumed outside footer platform icons (WhatsApp FAB). */
export const BUILDER_WHATSAPP_FIELD = { key: "whatsapp", label: "WhatsApp" }

export const FEATURE_TOGGLES = [
  { key: "pickup", label: "Enable Pickup" },
  { key: "delivery", label: "Enable Delivery" },
  { key: "curbside", label: "Enable Curbside" },
  { key: "driveThru", label: "Is Drive Thru Enabled" },
  { key: "dineIn", label: "Enable Dine In" },
  { key: "loyalty", label: "Loyalty" },
  { key: "wallet", label: "Wallet" },
  { key: "headerOrderControls", label: "Header Delivery/Pickup on menu pages" },
]

const emptyAppLink = () => ({
  url: "",
  label: createEmptyLocalized(),
  imageUrl: "",
})

export const createEmptyWebsiteForm = () => ({
  templateId: "classic-storefront",
  brand: { ...WULOOJ_BRAND },
  theme: {
    primaryColor: WULOOJ_THEME.primaryColor,
    secondaryColor: WULOOJ_THEME.secondaryColor,
    primaryLight: WULOOJ_THEME.primaryLight,
    primaryDeep: WULOOJ_THEME.primaryDeep,
    fontFamily: WULOOJ_THEME.fontFamily,
    fontDisplay: WULOOJ_THEME.fontDisplay,
    buttons: { ...WULOOJ_THEME.buttons },
    inputs: { ...WULOOJ_THEME.inputs },
    radios: { ...WULOOJ_THEME.radios },
    pageBackground: { ...WULOOJ_THEME.pageBackground },
  },
  topAd: {
    backgroundColor: WULOOJ_TOP_AD.backgroundColor,
    textColor: WULOOJ_TOP_AD.textColor,
    messages: WULOOJ_TOP_AD.messages.map(message => ({ ...message })),
  },
  hero: {
    layout: WULOOJ_HERO.layout,
    useMenuBanners: WULOOJ_HERO.useMenuBanners,
    headline: { ...WULOOJ_HERO.headline },
    subheadline: { ...WULOOJ_HERO.subheadline },
    ctaText: { ...WULOOJ_HERO.ctaText },
    ctaLink: WULOOJ_HERO.ctaLink,
    backgroundImage: WULOOJ_HERO.backgroundImage,
    badgeFallback: { ...WULOOJ_HERO.badgeFallback },
  },
  contact: {
    email: WULOOJ_CONTACT.email,
    phone: WULOOJ_CONTACT.phone,
    address: { ...WULOOJ_CONTACT.address },
    poBox: WULOOJ_CONTACT.poBox,
    cityLine: { ...WULOOJ_CONTACT.cityLine },
    mapEmbed: WULOOJ_CONTACT.mapEmbed,
  },
  social: { ...WULOOJ_SOCIAL },
  footer: {
    backgroundColor: WULOOJ_FOOTER.backgroundColor,
    textColor: WULOOJ_FOOTER.textColor,
    about: { ...WULOOJ_FOOTER.about },
    appLinks: {
      ios: { ...WULOOJ_FOOTER.appLinks.ios, label: { ...WULOOJ_FOOTER.appLinks.ios.label } },
      android: {
        ...WULOOJ_FOOTER.appLinks.android,
        label: { ...WULOOJ_FOOTER.appLinks.android.label },
      },
    },
    policies: {
      title: { ...WULOOJ_FOOTER.policies.title },
      links: WULOOJ_FOOTER.policies.links.map(link => ({
        label: { ...link.label },
        href: link.href,
      })),
    },
    importantLinks: {
      title: { ...WULOOJ_FOOTER.importantLinks.title },
      links: WULOOJ_FOOTER.importantLinks.links.map(link => ({
        label: { ...link.label },
        href: link.href,
      })),
    },
    social: {
      title: { ...WULOOJ_FOOTER.social.title },
      platforms: WULOOJ_FOOTER.social.platforms.map(platform => ({ ...platform })),
    },
    payments: WULOOJ_FOOTER.payments.map(payment => ({ ...payment })),
    copyright: { ...WULOOJ_FOOTER.copyright },
    vatNumber: WULOOJ_FOOTER.vatNumber,
    commercialRegisterNumber: WULOOJ_FOOTER.commercialRegisterNumber,
  },
  content: {
    footer: { about: { ...WULOOJ_CONTENT.footer.about } },
    sections: {
      categories: { ...WULOOJ_CONTENT.sections.categories },
      featured: { ...WULOOJ_CONTENT.sections.featured },
      offers: { ...WULOOJ_CONTENT.sections.offers },
    },
    sectionSubtitles: {
      featured: { ...WULOOJ_CONTENT.sectionSubtitles.featured },
      offers: { ...WULOOJ_CONTENT.sectionSubtitles.offers },
    },
    offerTitleFallback: { ...WULOOJ_CONTENT.offerTitleFallback },
    promoMessages: WULOOJ_CONTENT.promoMessages.map(message => ({ ...message })),
    trustStrip: {
      label: { ...WULOOJ_CONTENT.trustStrip.label },
      items: WULOOJ_CONTENT.trustStrip.items.map(item => ({
        title: { ...item.title },
        copy: { ...item.copy },
      })),
    },
  },
  seo: {
    metaTitle: { ...WULOOJ_SEO.metaTitle },
    metaDescription: { ...WULOOJ_SEO.metaDescription },
    ogImage: WULOOJ_SEO.ogImage,
  },
  template: {
    sectionOrder: [
      "orderContextBar",
      "hero",
      "categoryGrid",
      "featuredItems",
      "offerBanner",
      "whatsappFab",
    ],
    sectionVisibility: {
      orderContextBar: false,
      hero: true,
      offerBanner: true,
      categoryGrid: true,
      featuredItems: true,
      offerItems: false,
      whatsappFab: true,
    },
  },
  features: FEATURE_TOGGLES.reduce((acc, item) => {
    acc[item.key] = item.key === "wallet" ? false : true
    return acc
  }, {}),
  animations: { ...DEFAULT_WEBSITE_ANIMATIONS },
  webThemeSlug: "",
  preview: {
    websiteUrl: "",
  },
})

export const cloneWebsiteConfig = config =>
  JSON.parse(JSON.stringify(config || {}))

const isObject = value =>
  value && typeof value === "object" && !Array.isArray(value)

export const deepMergeForm = (base, patch) => {
  if (!patch) return cloneWebsiteConfig(base)
  const output = cloneWebsiteConfig(base)
  Object.keys(patch).forEach(key => {
    const value = patch[key]
    if (Array.isArray(value)) {
      output[key] = cloneWebsiteConfig(value)
    } else if (isObject(value) && isObject(output[key])) {
      output[key] = deepMergeForm(output[key], value)
    } else if (value !== undefined) {
      output[key] = value
    }
  })
  return output
}

export const normalizeWebsiteBuilderForm = (input = {}) => {
  const merged = deepMergeForm(createEmptyWebsiteForm(), input)

  if (!merged.content.promoMessages?.length && merged.topAd?.messages?.length) {
    merged.content.promoMessages = cloneWebsiteConfig(merged.topAd.messages)
  }
  if (!merged.topAd.messages?.length && merged.content.promoMessages?.length) {
    merged.topAd.messages = cloneWebsiteConfig(merged.content.promoMessages)
  }

  if (
    merged.content.footer?.about &&
    (!merged.footer.about?.en && !merged.footer.about?.ar)
  ) {
    merged.footer.about = { ...merged.content.footer.about }
  }
  merged.content.footer.about = { ...merged.footer.about }

  merged.template = {
    ...(merged.template || {}),
    sectionOrder: normalizeHomeSectionOrder(
      merged.template?.sectionOrder,
      createEmptyWebsiteForm().template.sectionOrder
    ),
  }

  // Keep tenant-selected layout when implemented on the storefront; otherwise default.
  merged.templateId = BUILDER_AVAILABLE_LAYOUT_IDS.has(merged.templateId)
    ? merged.templateId
    : BUILDER_TEMPLATE_ID

  const heroLayout = String(merged.hero?.layout || "").trim()
  merged.hero = {
    ...(merged.hero || {}),
    layout: VALID_HERO_LAYOUTS.has(heroLayout) ? heroLayout : "carousel",
  }

  return merged
}

export const normalizeWebsiteUrl = (value = "") => {
  const trimmed = String(value || "").trim()
  if (!trimmed) return ""
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  return `https://${trimmed.replace(/^\/+/, "")}`
}

export const resolveWebsitePreviewUrl = (form, tenantWebsiteUrl = "") => {
  const configured = normalizeWebsiteUrl(form?.preview?.websiteUrl)
  if (configured) return configured
  return normalizeWebsiteUrl(tenantWebsiteUrl)
}

const sectionLabel = (sectionId, sectionOptions = []) =>
  sectionOptions.find(item => item.id === sectionId)?.label || sectionId

export const getTemplateSwitchSummary = (
  fromTemplateId,
  toTemplateId,
  templates = [],
  sectionOptions = []
) => {
  if (!fromTemplateId || !toTemplateId || fromTemplateId === toTemplateId) {
    return null
  }

  const fromTemplate = templates.find(item => item.id === fromTemplateId)
  const toTemplate = templates.find(item => item.id === toTemplateId)
  if (!fromTemplate || !toTemplate) return null

  const fromSections = new Set(fromTemplate.defaultSectionOrder || [])
  const toSections = new Set(toTemplate.defaultSectionOrder || [])

  return {
    fromName: fromTemplate.name,
    toName: toTemplate.name,
    heroStyle: toTemplate.heroStyle || "",
    categoryStyle: toTemplate.categoryStyle || "",
    highlights: Array.isArray(toTemplate.highlights) ? toTemplate.highlights : [],
    addedSections: [...toSections]
      .filter(sectionId => !fromSections.has(sectionId))
      .map(sectionId => sectionLabel(sectionId, sectionOptions)),
    removedSections: [...fromSections]
      .filter(sectionId => !toSections.has(sectionId))
      .map(sectionId => sectionLabel(sectionId, sectionOptions)),
  }
}

export const getDeepValue = (obj, path) =>
  String(path || "")
    .split(".")
    .reduce((acc, key) => (acc == null ? acc : acc[key]), obj)

export const setDeepValue = (obj, path, value) => {
  const draft = cloneWebsiteConfig(obj)
  const parts = String(path || "").split(".")
  let cursor = draft
  for (let i = 0; i < parts.length - 1; i += 1) {
    const part = parts[i]
    cursor[part] = cursor[part] ? { ...cursor[part] } : {}
    cursor = cursor[part]
  }
  cursor[parts[parts.length - 1]] = value
  return draft
}

/** Keep primary-driven UI tokens in sync when the main brand color changes. */
export const syncPrimaryThemeColor = (form, color) => {
  let next = setDeepValue(form, "theme.primaryColor", color)
  next = setDeepValue(next, "theme.buttons.backgroundColor", color)
  next = setDeepValue(next, "theme.radios.selectedBorderColor", color)
  next = setDeepValue(next, "theme.radios.selectedTextColor", color)
  next = setDeepValue(next, "theme.radios.dotColor", color)
  return next
}

/** Apply a global web theme preset to the builder form (colors + promo/footer tints). */
export const applyWebThemeToForm = (form, webTheme) => {
  if (!webTheme) return cloneWebsiteConfig(form)

  let next = cloneWebsiteConfig(form)
  next.webThemeSlug = webTheme.slug || ""

  if (webTheme.theme && typeof webTheme.theme === "object") {
    next.theme = {
      ...(next.theme || {}),
      ...cloneWebsiteConfig(webTheme.theme),
      buttons: {
        ...(next.theme?.buttons || {}),
        ...(webTheme.theme.buttons || {}),
      },
      inputs: {
        ...(next.theme?.inputs || {}),
        ...(webTheme.theme.inputs || {}),
      },
      radios: {
        ...(next.theme?.radios || {}),
        ...(webTheme.theme.radios || {}),
      },
      pageBackground: {
        ...(next.theme?.pageBackground || {}),
        ...(webTheme.theme.pageBackground || {}),
      },
    }
  }

  if (webTheme.topAd) {
    next.topAd = {
      ...(next.topAd || {}),
      backgroundColor:
        webTheme.topAd.backgroundColor || next.topAd?.backgroundColor || "",
      textColor: webTheme.topAd.textColor || next.topAd?.textColor || "",
    }
  }

  if (webTheme.footer) {
    next.footer = {
      ...(next.footer || {}),
      backgroundColor:
        webTheme.footer.backgroundColor || next.footer?.backgroundColor || "",
      textColor: webTheme.footer.textColor || next.footer?.textColor || "",
    }
  }

  return next
}

/** Extract full website builder snapshot for DB storage. */
export const extractWebsiteSnapshot = (form = {}) => {
  const snapshot = cloneWebsiteConfig(form)
  delete snapshot.status
  return snapshot
}

/** Legacy color-only snapshots (before full-config save). */
export const isLegacyThemeBaseline = (snapshot = {}) =>
  !!snapshot?.theme &&
  !snapshot.brand &&
  !snapshot.content &&
  !snapshot.hero &&
  !snapshot.footer?.about

const applyLegacyThemeBaselineToForm = (form, baseline) => {
  const next = cloneWebsiteConfig(form)
  next.webThemeSlug = baseline.webThemeSlug || ""

  if (baseline.theme && typeof baseline.theme === "object") {
    next.theme = cloneWebsiteConfig(baseline.theme)
  }

  if (baseline.topAd) {
    next.topAd = {
      ...(next.topAd || {}),
      ...(baseline.topAd.backgroundColor != null
        ? { backgroundColor: baseline.topAd.backgroundColor }
        : {}),
      ...(baseline.topAd.textColor != null ? { textColor: baseline.topAd.textColor } : {}),
    }
  }

  if (baseline.footer) {
    next.footer = {
      ...(next.footer || {}),
      ...(baseline.footer.backgroundColor != null
        ? { backgroundColor: baseline.footer.backgroundColor }
        : {}),
      ...(baseline.footer.textColor != null ? { textColor: baseline.footer.textColor } : {}),
    }
  }

  return next
}

/** Restore a saved website snapshot onto the builder form (draft only). */
export const applyWebsiteSnapshotToForm = (form, snapshot) => {
  if (!snapshot) return cloneWebsiteConfig(form)
  if (isLegacyThemeBaseline(snapshot)) {
    return applyLegacyThemeBaselineToForm(form, snapshot)
  }
  return normalizeWebsiteBuilderForm(snapshot)
}

/** @deprecated Use extractWebsiteSnapshot */
export const extractThemeBaseline = extractWebsiteSnapshot

/** @deprecated Use applyWebsiteSnapshotToForm */
export const applyThemeBaselineToForm = applyWebsiteSnapshotToForm

export const setLocalizedDeep = (obj, path, locale, value) => {
  const current = getDeepValue(obj, path) || createEmptyLocalized()
  return setDeepValue(obj, path, { ...current, [locale]: value })
}

export const syncPromoMessages = (obj, messages) => {
  const next = cloneWebsiteConfig(obj)
  next.topAd = { ...(next.topAd || {}), messages: cloneWebsiteConfig(messages) }
  next.content = {
    ...(next.content || {}),
    promoMessages: cloneWebsiteConfig(messages),
  }
  return next
}
